'use client';

import { useMemo } from 'react';

import posthog, { type PostHog } from 'posthog-js';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { CitOrigin } from '@hub/common/src/types/cit-public-api';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST;

// escape a string for use inside a RegExp
function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// regex source matching a path segment as browsers may write it in location.href: unreserved characters
// as-is, anything else either raw or percent-encoded (e.g. ' vs %27, é vs %C3%A9)
function pathSegmentPattern(segment: string) {
  return Array.from(segment)
    .map((char) => {
      if (/^[A-Za-z0-9\-_.~]$/.test(char)) {
        return escapeRegExp(char);
      }

      const encoded = Array.from(new TextEncoder().encode(char))
        .map((byte) => '%' + byte.toString(16).toUpperCase().padStart(2, '0'))
        .join('');

      return `(?:${escapeRegExp(char)}|${encoded})`;
    })
    .join('');
}

// build account specific url regex for the autocapture url allowlist in posthog. urlSegment is the segment
// that identifies the account in the path: its slug, or its id when it has no slug
export function buildAccountUrlRegex(urlSegment: string) {
  const segment = pathSegmentPattern(urlSegment);

  return new RegExp(
    `(?:^|\\/)(?:` +
      `${segment}` +
      `|search\\/${segment}` +
      `|classifications\\/[^/?#]+\\/${segment}` +
      `)(?:\\/|$|\\?|#)`,
    'i'
  );
}

// the page a url shows, for deciding whether a navigation is a new pageview. on entry and profile
// pages ?query= only carries the back link to the results, so adding or clearing it is the same page
export function pageKey(pathname: string, search: URLSearchParams | string) {
  const params = new URLSearchParams(search);
  if (/\/(entry|instance)\//.test(pathname)) {
    params.delete('query');
  }
  return `${pathname}?${params.toString()}`;
}

// the last search reported, as list scope plus term. kept per document rather than per list instance, so a
// list remounting with the same query (back/forward) does not report the search again
let lastSearch: string | null = null;

export function trackSearch(
  client: PostHog,
  scope: Record<string, string | null | undefined>,
  searchTerm: string,
  successful: boolean
) {
  const key = JSON.stringify({ ...scope, searchTerm });
  if (key === lastSearch) {
    return;
  }
  lastSearch = key;
  client.capture('search', { search_term: searchTerm, successful });
}

// posthog-js stamps document.referrer on every event, so only the first pageview this document sends
// may keep it; a per-instance flag would hand the landing referrer to every account visited after
let pageviewSent = false;

export function capturePageview(
  client: PostHog,
  currentUrl: string,
  properties: Record<string, string> = {}
) {
  if (!pageviewSent) {
    client.capture('$pageview', { $current_url: currentUrl, ...properties });
    // a pageview dropped while consent is pending did not send the referrer
    pageviewSent = client.is_capturing();
    return;
  }

  client.capture('$pageview', {
    $current_url: currentUrl,
    ...properties,
    $referrer: '$direct',
    $referring_domain: '$direct'
  });
}

interface SyncProAnalyticsClientArgs {
  accountId?: string;
  // only used to scope autocapture to the account's urls; event tracking stays keyed on accountId
  accountSlug?: string;
  citOrigin?: string;
  country?: string;
  region?: string;
  regionCode?: string;
  city?: string;
  latitude?: string;
  longitude?: string;
}

export function syncProAnalyticsClient(
  client: PostHog,
  {
    accountId,
    accountSlug,
    citOrigin,
    country,
    region,
    regionCode,
    city,
    latitude,
    longitude
  }: SyncProAnalyticsClientArgs
) {
  const groups = client.getGroups();

  // an omitted property keeps whatever is registered; the modal carousels only know the account
  const unchanged = (key: string, value: string | undefined) =>
    value === undefined || client.get_property(key) === value;

  // no-op when groups are already set and current values match the provided values. accountSlug is
  // deliberately left out of the comparison: it is 1:1 with the account and cannot change under a
  // visitor mid-session, so a matching accountId means the autocapture allowlist is already correct
  if (
    groups.account === accountId &&
    unchanged('country', country) &&
    unchanged('region', region) &&
    unchanged('region_code', regionCode) &&
    unchanged('city', city) &&
    unchanged('latitude', latitude) &&
    unchanged('longitude', longitude) &&
    unchanged('cit_origin', citOrigin)
  ) {
    return;
  }

  // only set properties that are provided
  client.register({
    ...(country !== undefined ? { country } : {}),
    ...(region !== undefined ? { region } : {}),
    ...(regionCode !== undefined ? { region_code: regionCode } : {}),
    ...(latitude !== undefined ? { latitude } : {}),
    ...(longitude !== undefined ? { longitude } : {}),
    ...(city !== undefined ? { city } : {}),
    ...(citOrigin !== undefined ? { cit_origin: citOrigin } : {})
  });

  client.resetGroups();

  if (accountId) {
    client.group('account', accountId, { accountId });

    client.set_config({
      // re-enable autocapture events, on all pages for HUB Pro, scope to the account's pages for HUB/iframe
      autocapture:
        citOrigin === CitOrigin.HUBPRO
          ? true
          : {
              url_allowlist: [buildAccountUrlRegex(accountSlug || accountId)]
            }
    });
  }
}

// undo syncProAnalyticsClient when leaving an account: drop the account group and stop autocapture
export function resetProAnalyticsClient(client: PostHog) {
  client.resetGroups();
  client.set_config({ autocapture: false });
}

// while a modal carousel has swiped, it owns the url and the account group. these hold the url it last
// moved to and, when consent let it through, the last pageview it sent; the carousel clears them on unmount
let modalUrl: string | null = null;
let modalPageviewUrl: string | null = null;

// same page whichever way either side encoded the path or query
function isSameUrl(a: string, b: string) {
  const ua = new URL(a);
  const ub = new URL(b);
  return pageKey(ua.pathname, ua.search) === pageKey(ub.pathname, ub.search);
}

// the carousel owns exactly the url it last moved to: another url is another route's to report
export function modalOwnsUrl(url: string) {
  return modalUrl !== null && isSameUrl(url, modalUrl);
}

// true when url is where the carousel last swiped to and no pageview for it went out (consent was pending)
export function modalPageviewPending(url: string) {
  return (
    modalUrl !== null &&
    isSameUrl(url, modalUrl) &&
    !(modalPageviewUrl !== null && isSameUrl(url, modalPageviewUrl))
  );
}

export function forgetModalNavigation() {
  modalUrl = null;
  modalPageviewUrl = null;
}

// used in modal carousels to track navigation, since they don't update the url using next/navigation
export function trackProAnalyticsModalNavigation(
  client: PostHog,
  {
    accountId,
    accountSlug,
    currentUrl,
    citOrigin,
    country,
    region,
    regionCode,
    city,
    latitude,
    longitude
  }: SyncProAnalyticsClientArgs & { currentUrl: string }
) {
  syncProAnalyticsClient(client, {
    accountId,
    accountSlug,
    citOrigin,
    country,
    region,
    regionCode,
    city,
    latitude,
    longitude
  });

  modalUrl = currentUrl;
  capturePageview(client, currentUrl);

  if (client.is_capturing()) {
    modalPageviewUrl = currentUrl;
  }
}

// wrapper around default posthog provider to to add our config and capture pageview events after turning off automatic pageview
export function PostHogAnalyticsProvider({ children }: { children: React.ReactNode }) {
  if (!POSTHOG_KEY || !POSTHOG_HOST) {
    throw new Error('NEXT_PUBLIC_POSTHOG_KEY and NEXT_PUBLIC_POSTHOG_HOST must be set');
  }

  const posthogClient = usePostHog();

  const client = useMemo(() => {
    if (posthogClient.__loaded) {
      return posthogClient;
    }

    return posthog.init(POSTHOG_KEY, {
      // default persistence (localStorage+cookie) only writes once the visitor accepts; until then,
      // and after a reject, posthog-js keeps its state in memory and sends cookieless events
      cookieless_mode: 'on_reject',
      // scope the id and consent cookies to this host: the default would share them across every
      // <customer>.hubpro.io site
      cross_subdomain_cookie: false,
      api_host: POSTHOG_HOST,
      defaults: '2026-01-30',

      opt_out_capturing_persistence_type: 'cookie',

      // opting in / out of cookies preferences will last for 30 days, then it will start showing the cookie banner again
      cookie_expiration: 30,

      // disable automatic pageview, because we need to have group set before pageview is captured
      capture_pageview: false,

      // disable autocapture events i.e "Clicked SVG", "Rage Clicked", etc
      autocapture: false,

      // storage restores the last account's group on a hard load; the page's own sync sets it again
      loaded: (loadedClient) => loadedClient.resetGroups()
    });
  }, [posthogClient]);

  return <PostHogProvider client={client}>{children}</PostHogProvider>;
}
